// src/hooks/useChat.ts
import { useEffect, useState, useCallback } from 'react';

export interface ChatMessage {
  id: string;
  room_id: string;
  sender_id: string;
  content: string;
  type: string;
  created_at: number;
}

export function useChat(userId: string | null, roomId: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(true);

  // 1. 標記目前房間為已讀
  const markAsRead = useCallback(async () => {
    if (!userId || !roomId) return;
    try {
      await fetch('/api/chat/read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, roomId }),
      });
    } catch (err) {
      console.error('更新已讀狀態失敗:', err);
    }
  }, [userId, roomId]);

  // 2. 拉取訊息
  const fetchMessages = useCallback(async () => {
    if (!userId || !roomId) return;
    try {
      // 加上 _t 時間戳記防止瀏覽器快取
      const res = await fetch(`/api/chat/messages?roomId=${roomId}&_t=${Date.now()}`);

      if (res.status === 200 || res.status === 304) {
        setIsConnected(true);
        if (res.status === 200) {
          const data = await res.json();
          setMessages(data.messages || []);
        }
      } else {
        setIsConnected(false);
      }
    } catch (err) {
      console.error('拉取訊息失敗:', err);
      setIsConnected(false);
    }
  }, [userId, roomId]);

  // 3. 輪詢訊息
  useEffect(() => {
    if (!roomId || !userId) return;

    fetchMessages();
    const timer = setInterval(fetchMessages, 2000);

    return () => clearInterval(timer);
  }, [roomId, userId, fetchMessages]);

  // 4. 發送訊息
  const sendMessage = useCallback(
    async (content: string, type = 'text') => {
      if (!userId || !roomId || !content.trim()) return;

      const tempMsg: ChatMessage = {
        id: Date.now().toString(),
        room_id: roomId,
        sender_id: userId,
        content,
        type,
        created_at: Date.now(),
      };

      // 樂觀更新畫面
      setMessages((prev) => [...prev, tempMsg]);

      try {
        const res = await fetch('/api/chat/messages', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            roomId,
            senderId: userId,
            content,
            type,
          }),
        });

        if (res.ok) {
          // 訊息發送成功後，同步更新發送者的已讀時間
          await markAsRead();
          // 重新整理最新訊息列表
          fetchMessages();
        } else {
          console.error('發送失敗，狀態碼:', res.status);
        }
      } catch (err) {
        console.error('發送訊息失敗:', err);
      }
    },
    [userId, roomId, fetchMessages, markAsRead]
  );

  return {
    messages,
    isConnected,
    sendMessage,
    markAsRead, // 導出 markAsRead 供 UI 手動呼叫
  };
}